import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Coffee, Info, X } from "lucide-react";
import { api } from "../lib/api";
import { getPhone, setPhone as savePhone, getName, setName as saveName } from "../lib/session";
import { toast } from "sonner";

const Rewards = () => {
    const { slug } = useParams();
    const nav = useNavigate();
    const [phone, setPhoneState] = useState(getPhone());
    const [entered, setEntered] = useState(!!getPhone());
    const [balance, setBalance] = useState(0);
    const [items, setItems] = useState([]);
    const [tenantName, setTenantName] = useState("");
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(null); // item pending confirm
    const [ticket, setTicket] = useState(null); // successful redemption receipt

    const digits = phone.replace(/\D/g, "");
    const valid = digits.length >= 8 && digits.length <= 15;

    const loadAll = async (d) => {
        setLoading(true);
        try {
            const [cat, bal] = await Promise.all([
                api.get("/catalog"),
                d ? api.get(`/beans/${d}`) : Promise.resolve({ data: { balance: 0 } }),
            ]);
            setItems(cat.data.items || []);
            setTenantName(cat.data.tenant?.name || "");
            setBalance(bal.data.balance || 0);
        } catch {
            toast.error("Couldn't load rewards");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (entered && valid) loadAll(digits);
        else loadAll(null);
    }, [entered, digits, valid]); // eslint-disable-line

    const submitPhone = () => {
        if (!valid) return;
        savePhone(digits);
        setEntered(true);
    };

    const redeem = async (item) => {
        try {
            const r = await api.post("/redemptions", {
                phone: digits,
                name: getName() || "",
                item_id: item.id,
            });
            setBalance(r.data.new_balance);
            setTicket(r.data);
            setConfirming(null);
            toast.success("Redeemed! Show your ticket at the counter.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't redeem");
            setConfirming(null);
        }
    };

    return (
        <div className="min-h-screen bg-[#F9F6F0] text-[#2C1A12]">
            <header className="border-b border-[#E8DCCB]/60">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <button
                        data-testid="rewards-back-btn"
                        onClick={() => nav(`/${slug}`)}
                        className="flex items-center gap-2 text-sm font-semibold text-[#8C7A72] hover:text-[#2C1A12]"
                    >
                        <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> Back to café
                    </button>
                    <div className="font-display font-black text-xl">
                        Coffee<span className="text-[#FF4F18]">in7.</span>
                    </div>
                    <div className="w-20" />
                </div>
            </header>

            <div className="max-w-3xl mx-auto px-6 py-8">
                {!entered ? (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                            {tenantName} · Rewards
                        </div>
                        <h1 className="mt-3 font-display font-black text-4xl leading-tight">
                            Spend your <span className="text-[#FF4F18]">Beans</span>.
                        </h1>
                        <p className="mt-3 text-[#8C7A72] max-w-md">
                            Every coffee you order earns 7 Beans. Redeem them here for merch, whole beans, or a free
                            coffee at the counter.
                        </p>
                        <div className="mt-8 max-w-md">
                            <label className="text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72]">
                                Your mobile number
                            </label>
                            <input
                                data-testid="rewards-phone-input"
                                type="tel"
                                inputMode="numeric"
                                placeholder="0400 000 000"
                                value={phone}
                                onChange={(e) => setPhoneState(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && submitPhone()}
                                className="mt-3 w-full text-3xl font-display font-black text-[#2C1A12] placeholder:text-[#E8DCCB] bg-transparent border-b-4 border-[#E8DCCB] focus:border-[#FF4F18] outline-none py-3"
                            />
                            <button
                                data-testid="rewards-continue-btn"
                                onClick={submitPhone}
                                disabled={!valid}
                                className={`mt-6 w-full py-4 rounded-full font-display font-bold flex items-center justify-center gap-2 ${
                                    valid
                                        ? "bg-[#FF4F18] text-white active:scale-95 shadow-[0_12px_32px_rgba(255,79,24,0.32)]"
                                        : "bg-[#E8DCCB] text-[#8C7A72] cursor-not-allowed"
                                }`}
                            >
                                Show my Beans <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                            </button>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                        {/* Balance hero */}
                        <div className="rounded-3xl bg-gradient-to-br from-[#2C1A12] to-[#0E0906] text-white p-6 relative overflow-hidden">
                            <div className="pointer-events-none absolute -right-8 -top-8 text-[200px] opacity-[0.06] leading-none">
                                🫘
                            </div>
                            <div className="relative">
                                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                                    {tenantName} · Your Beans
                                </div>
                                <div className="mt-3 flex items-baseline gap-2">
                                    <div className="font-display font-black text-6xl leading-none" data-testid="rewards-balance">
                                        {balance.toLocaleString()}
                                    </div>
                                    <div className="font-display font-bold text-lg text-white/60">Beans</div>
                                </div>
                                <div className="mt-3 text-xs text-white/60">
                                    Earn <strong className="text-white">7 Beans</strong> for every coffee you order ·
                                    ···{digits.slice(-4)}
                                </div>
                                <button
                                    data-testid="rewards-change-phone"
                                    onClick={() => {
                                        setPhoneState("");
                                        setEntered(false);
                                        setBalance(0);
                                    }}
                                    className="mt-2 text-[10px] text-white/50 underline"
                                >
                                    Not you? Change number
                                </button>
                            </div>
                        </div>

                        <div className="mt-8 flex items-baseline justify-between">
                            <h2 className="font-display font-black text-2xl">Rewards catalog</h2>
                            <a
                                href={`/${slug}/menu`}
                                className="text-xs font-display font-bold text-[#FF4F18] flex items-center gap-1 hover:underline"
                            >
                                <Coffee className="h-3 w-3" strokeWidth={2.5} /> Order to earn
                            </a>
                        </div>

                        {loading ? (
                            <div className="mt-8 text-[#8C7A72]">Loading rewards…</div>
                        ) : items.length === 0 ? (
                            <div className="mt-8 rounded-3xl bg-white border border-[#E8DCCB] p-10 text-center">
                                <Info className="h-6 w-6 text-[#8C7A72] mx-auto" strokeWidth={2} />
                                <div className="mt-3 font-display font-bold">No rewards yet</div>
                                <p className="mt-1 text-xs text-[#8C7A72]">
                                    This café hasn&apos;t set up their catalog yet. Check back soon!
                                </p>
                            </div>
                        ) : (
                            <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="rewards-catalog-grid">
                                {items.map((it) => {
                                    const affordable = balance >= it.cost_beans;
                                    const toNeed = Math.max(0, it.cost_beans - balance);
                                    return (
                                        <button
                                            key={it.id}
                                            data-testid={`rewards-item-${it.id}`}
                                            onClick={() => affordable && setConfirming(it)}
                                            disabled={!affordable}
                                            className={`text-left rounded-3xl border p-5 transition-all ${
                                                affordable
                                                    ? "bg-white border-[#E8DCCB] hover:border-[#FF4F18] hover:shadow-[0_16px_40px_rgba(255,79,24,0.08)] active:scale-[0.99]"
                                                    : "bg-[#F9F6F0] border-[#E8DCCB]/60 opacity-70 cursor-not-allowed"
                                            }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="h-14 w-14 rounded-2xl bg-[#F9F6F0] border border-[#E8DCCB] flex items-center justify-center text-3xl shrink-0">
                                                    {it.emoji || "🎁"}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-display font-black text-base leading-tight">
                                                        {it.name}
                                                    </div>
                                                    {it.description && (
                                                        <div className="mt-1 text-xs text-[#8C7A72] line-clamp-2">
                                                            {it.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mt-4 flex items-center justify-between">
                                                <div className="font-display font-black text-lg text-[#FF4F18] flex items-center gap-1">
                                                    <span>🫘</span> {it.cost_beans}
                                                </div>
                                                {affordable ? (
                                                    <div className="rounded-full bg-[#FF4F18] text-white px-4 py-1.5 text-xs font-display font-bold">
                                                        Redeem
                                                    </div>
                                                ) : (
                                                    <div className="text-[10px] text-[#8C7A72] font-display font-bold">
                                                        {toNeed} more to unlock
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </motion.div>
                )}
            </div>

            {/* Confirm redemption modal */}
            <AnimatePresence>
                {confirming && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-[#2C1A12]/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
                    >
                        <motion.div
                            initial={{ y: 40 }}
                            animate={{ y: 0 }}
                            exit={{ y: 40 }}
                            className="w-full sm:max-w-md bg-[#F9F6F0] rounded-t-3xl sm:rounded-3xl p-6"
                            data-testid="rewards-confirm-modal"
                        >
                            <div className="text-center">
                                <div className="text-6xl">{confirming.emoji || "🎁"}</div>
                                <h2 className="mt-4 font-display font-black text-2xl">Redeem this reward?</h2>
                                <div className="mt-1 text-lg font-display font-bold">{confirming.name}</div>
                                <div className="mt-4 rounded-2xl bg-white border border-[#E8DCCB] p-4">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-[#8C7A72]">Balance</span>
                                        <span className="font-display font-bold">{balance.toLocaleString()} 🫘</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm mt-1 text-[#FF4F18]">
                                        <span>Cost</span>
                                        <span className="font-display font-bold">-{confirming.cost_beans} 🫘</span>
                                    </div>
                                    <div className="mt-2 pt-2 border-t border-[#E8DCCB] flex items-center justify-between">
                                        <span className="text-xs text-[#8C7A72] font-bold uppercase tracking-[0.2em]">
                                            After
                                        </span>
                                        <span className="font-display font-black">
                                            {(balance - confirming.cost_beans).toLocaleString()} 🫘
                                        </span>
                                    </div>
                                </div>
                                <p className="mt-4 text-xs text-[#8C7A72]">
                                    Show the confirmation ticket to a barista at {tenantName} to collect your reward.
                                </p>
                            </div>
                            <div className="mt-6 flex gap-3">
                                <button
                                    onClick={() => setConfirming(null)}
                                    className="flex-1 py-3 rounded-full bg-white border-2 border-[#E8DCCB] font-display font-bold active:scale-95"
                                >
                                    Cancel
                                </button>
                                <button
                                    data-testid="rewards-confirm-btn"
                                    onClick={() => redeem(confirming)}
                                    className="flex-[2] py-3 rounded-full bg-[#FF4F18] text-white font-display font-bold active:scale-95 flex items-center justify-center gap-2"
                                >
                                    <Check className="h-4 w-4" strokeWidth={2.5} /> Confirm redeem
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}

                {/* Ticket / success screen */}
                {ticket && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-[#2C1A12]/85 backdrop-blur flex items-center justify-center p-6"
                        data-testid="rewards-ticket-modal"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="w-full max-w-md bg-[#F9F6F0] rounded-3xl p-6 relative"
                        >
                            <button
                                data-testid="rewards-ticket-close"
                                onClick={() => setTicket(null)}
                                className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center"
                            >
                                <X className="h-4 w-4" strokeWidth={2.5} />
                            </button>
                            <div className="text-center">
                                <div className="inline-flex items-center gap-2 rounded-full bg-[#1B995E]/10 border border-[#1B995E]/30 px-3 py-1.5">
                                    <span className="h-2 w-2 rounded-full bg-[#1B995E] animate-pulse" />
                                    <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#1B995E]">
                                        Redemption ticket · Show at counter
                                    </span>
                                </div>
                                <div className="mt-6 text-7xl">{ticket.item_emoji || "🎁"}</div>
                                <h2 className="mt-4 font-display font-black text-3xl leading-tight">
                                    {ticket.item_name}
                                </h2>
                                <div className="mt-1 text-sm text-[#8C7A72]">at {ticket.tenant_name}</div>

                                <div className="mt-6 rounded-2xl bg-[#2C1A12] text-white p-4">
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-bold">
                                        Code
                                    </div>
                                    <div className="mt-1 font-mono font-black text-4xl tracking-widest" data-testid="rewards-ticket-code">
                                        {ticket.code}
                                    </div>
                                </div>

                                <div className="mt-4 flex items-center justify-around text-xs">
                                    <div>
                                        <div className="text-[9px] uppercase tracking-[0.25em] text-[#8C7A72] font-bold">
                                            Spent
                                        </div>
                                        <div className="font-display font-black text-[#FF4F18]">
                                            {ticket.cost_beans} 🫘
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[9px] uppercase tracking-[0.25em] text-[#8C7A72] font-bold">
                                            Balance
                                        </div>
                                        <div className="font-display font-black">{ticket.new_balance.toLocaleString()} 🫘</div>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setTicket(null)}
                                className="mt-6 w-full py-3 rounded-full bg-[#FF4F18] text-white font-display font-bold active:scale-95"
                            >
                                Done
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Rewards;
